import React, { useState, useEffect, useRef, useCallback } from 'react';
import useMediaQuery from '../utils/useMediaQuery';
import { isSoundEnabled } from '../utils/soundPrefs';

// ─────────────────────────────────────────────────────────────────────────────
// Sprite sheet config
//
// Drop `beary-sprite.png` into /public/ and this just works.
//
// To find the right FRAME_W / FRAME_H: open the PNG, divide its pixel width
// by 8 (max columns) and its pixel height by 9 (rows). Then set the display
// size to whatever looks good — the math scales automatically.
// ─────────────────────────────────────────────────────────────────────────────
const SPRITE_SRC = '/beary-explorer-clean.png';
const SHEET_COLS = 8;    // columns in the sprite sheet
const SHEET_ROWS = 9;    // rows  in the sprite sheet
const DISPLAY_W  = 96;   // px — how wide  Beary renders on screen
const DISPLAY_H  = 104;  // px — how tall  Beary renders on screen

const DISPLAY_FRAME_H = DISPLAY_H;

// ─────────────────────────────────────────────────────────────────────────────
// Animation rows (matches the sprite sheet layout)
// row 0: idle       6 frames
// row 1: run-right  8 frames  (not used by mascot guide)
// row 2: run-left   8 frames  (not used by mascot guide)
// row 3: waving     4 frames
// row 4: jumping    5 frames
// row 5: failed     8 frames
// row 6: waiting    6 frames
// row 7: questing   6 frames  (running with a map)
// row 8: reading    6 frames
// ─────────────────────────────────────────────────────────────────────────────
const ANIMS = {
  idle:     { row: 0, frames: 6, fps: 7  },
  waving:   { row: 3, frames: 4, fps: 9  },
  jumping:  { row: 4, frames: 5, fps: 11 },
  failed:   { row: 5, frames: 8, fps: 6  },
  waiting:  { row: 6, frames: 6, fps: 5  },
  questing: { row: 7, frames: 6, fps: 10 },
  reading:  { row: 8, frames: 6, fps: 7  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Messages
// ─────────────────────────────────────────────────────────────────────────────
const IDLE_MESSAGES = [
  "Tap a country — I'll pack snacks! 🎒",
  "Where shall we go today? 🌍",
  "I hear Brazil has amazing music! 🎵",
  "Have you visited Japan yet? 🗾",
  "Let's find a country with tigers! 🐯",
  "Australia has kangaroos! 🦘",
  "Egypt has the tallest pyramids! 🔺",
  "France has the yummiest food! 🥐",
  "The world is huge — let's explore! ✈️",
];

function getMessages(appState, countryName) {
  switch (appState) {
    case 'loading':
      return [
        'Painting your adventure card… 🎨',
        'Almost ready — good things take time!',
      ];
    case 'error':
      return [
        "Oops! Let's try another place. 🗺️",
        "No worries — the world is big!",
      ];
    case 'country':
      return countryName
        ? [
            `You found ${countryName}! 🎉`,
            'Want to hear the story? Tap Listen!',
            "What's your favourite thing here?",
          ]
        : ['Nice discovery! 🌟', 'Want to hear the story?'];
    case 'landmark':
      return [
        'That place is famous! 🏛️',
        'Tap the card to learn more!',
      ];
    default:
      return IDLE_MESSAGES;
  }
}

function getAnim(appState) {
  switch (appState) {
    case 'loading':  return ANIMS.waiting;
    case 'error':    return ANIMS.failed;
    case 'country':  return ANIMS.reading;
    case 'landmark': return ANIMS.waving;
    default:         return ANIMS.idle;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
// ── Card tap reaction messages ────────────────────────────────────────────────
const CARD_REACTIONS = {
  animal:  { msg: "Cool creature! 🐾 Amazing animals live here!", anim: ANIMS.jumping  },
  food:    { msg: "Yum! That sounds delicious! 🍽️",              anim: ANIMS.waving   },
  landmark:{ msg: "Wow, that place looks incredible! 🏛️",        anim: ANIMS.waving   },
  fact:    { msg: "Whoa, I didn't know that! 🤯",                anim: ANIMS.jumping  },
  culture: { msg: "So many cool traditions here! 🎉",            anim: ANIMS.waving   },
};

/**
 * MascotGuide — Beary, the floating companion
 *
 * Props:
 *   state        — 'idle' | 'loading' | 'country' | 'landmark' | 'error'
 *   countryName  — string, used in country messages
 *   cardTap      — { type: 'animal'|'food'|'landmark'|'fact'|'culture', ts: number }
 *                  Changes each time a discovery card is opened
 *   onSuggest    — called when user taps Beary in idle state (pick a quest / random country)
 */
// Beary's voice clip (ElevenLabs, placed in /public/beary-voice.mp3)
const bearVoice = new Audio('/beary-voice.mp3');
bearVoice.volume = 0.85;

export default function MascotGuide({ state = 'idle', countryName, cardTap, onSuggest }) {
  const isMobile = useMediaQuery('(max-width: 640px)');
  const [frame,       setFrame]     = useState(0);
  const [msgIdx,      setMsgIdx]    = useState(0);
  const [bubble,      setBubble]    = useState(true);
  const [bounce,      setBounce]    = useState(false);
  const [loaded,      setLoaded]    = useState(false);
  const [reaction,    setReaction]  = useState(null); // { msg, anim } or null
  const [voicePlayed, setVoicePlayed] = useState(false); // play once on first tap
  const prevState                   = useRef(state);
  const prevCardTs                  = useRef(null);

  // Active animation: card reaction overrides the state animation
  const anim     = reaction ? reaction.anim : getAnim(state);
  const messages = reaction ? [reaction.msg] : getMessages(state, countryName);
  const message  = messages[msgIdx % messages.length];

  // ── Reset when app state changes ──────────────────────────────────────────
  useEffect(() => {
    if (prevState.current !== state) {
      setFrame(0);
      setMsgIdx(0);
      setBubble(true);
      setReaction(null);
      prevState.current = state;
    }
  }, [state]);

  // ── Card tap → brief excited reaction, then back to normal ───────────────
  useEffect(() => {
    if (!cardTap || cardTap.ts === prevCardTs.current) return;
    prevCardTs.current = cardTap.ts;

    const r = CARD_REACTIONS[cardTap.type];
    if (!r) return;

    setReaction(r);
    setFrame(0);
    setBubble(true);
    setBounce(true);
    setTimeout(() => setBounce(false), 400);
    // Return to normal after 3.5 s
    const t = setTimeout(() => setReaction(null), 3500);
    return () => clearTimeout(t);
  }, [cardTap]);

  // ── Sprite animation tick ─────────────────────────────────────────────────
  useEffect(() => {
    const id = setInterval(
      () => setFrame(f => (f + 1) % anim.frames),
      Math.round(1000 / anim.fps)
    );
    return () => clearInterval(id);
  }, [anim]);

  // ── Rotate idle messages every 6 s ───────────────────────────────────────
  useEffect(() => {
    if (state !== 'idle' || reaction) return;
    const id = setInterval(() => setMsgIdx(i => i + 1), 6000);
    return () => clearInterval(id);
  }, [state, reaction]);

  // ── Tap handler ───────────────────────────────────────────────────────────
  const handleTap = useCallback(() => {
    // Bounce animation
    setBounce(true);
    setTimeout(() => setBounce(false), 400);

    // Play Beary's voice on very first tap (browser needs a gesture).
    // Respects the global sound preference (UI_REVIEW §6).
    if (!voicePlayed && isSoundEnabled()) {
      bearVoice.currentTime = 0;
      bearVoice.play().catch(() => {}); // silently ignore if blocked
      setVoicePlayed(true);
    }

    if (state === 'idle' && onSuggest) {
      onSuggest();          // let App pick a random country / quest
    } else {
      setMsgIdx(i => i + 1); // cycle through messages
    }
    setBubble(true);
  }, [state, onSuggest, voicePlayed]);

  // ── Sprite background-position ────────────────────────────────────────────
  const bgSize = `${SHEET_COLS * DISPLAY_W}px ${SHEET_ROWS * DISPLAY_FRAME_H}px`;
  const bgPos  = `-${frame * DISPLAY_W}px -${anim.row * DISPLAY_FRAME_H}px`;

  return (
    <div style={{ ...s.wrap, ...(isMobile ? s.wrapMobile : {}) }} aria-live="polite">

      {/* ── Speech bubble ──────────────────────────────────────────────── */}
      {bubble && loaded && (
        <div style={{ ...s.bubble, ...(isMobile ? s.bubbleMobile : {}) }} key={message}>
          <span style={s.bubbleText}>{message}</span>
          <button
            style={s.dismiss}
            onClick={() => setBubble(false)}
            aria-label="Dismiss Beary's message"
          >
            ✕
          </button>
          {/* little tail pointing down-left toward Beary */}
          <div style={s.tail} />
        </div>
      )}

      {/* ── Beary sprite ───────────────────────────────────────────────── */}
      <button
        style={{ ...s.btn, transform: bounce ? 'scale(1.18) translateY(-10px)' : 'scale(1)' }}
        onClick={handleTap}
        aria-label="Tap Beary for a hint"
        title="Tap me!"
      >
        {/* Hidden preload to know when the image is ready */}
        <img
          src={SPRITE_SRC}
          alt=""
          style={{ display: 'none' }}
          onLoad={() => setLoaded(true)}
        />

        {loaded ? (
          <div
            style={{
              width:              DISPLAY_W,
              height:             DISPLAY_FRAME_H,
              backgroundImage:    `url(${SPRITE_SRC})`,
              backgroundRepeat:   'no-repeat',
              backgroundSize:     bgSize,
              backgroundPosition: bgPos,
              filter: 'drop-shadow(0 6px 14px rgba(0,0,0,0.28))',
            }}
          />
        ) : (
          /* Friendly fallback while the PNG loads */
          <div style={s.fallback}>🐻</div>
        )}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────────
const s = {
  wrap: {
    position:      'absolute',
    left:          16,
    bottom:        'calc(env(safe-area-inset-bottom, 0px) + 100px)',
    zIndex:        20,
    display:       'flex',
    flexDirection: 'column',
    alignItems:    'flex-start',
    gap:           0,
    pointerEvents: 'none',   // let globe clicks through by default
  },
  wrapMobile: {
    left: 8,
    bottom: 'calc(env(safe-area-inset-bottom, 0px) + 80px)',
    transform: 'scale(0.78)',
    transformOrigin: 'bottom left',
  },

  bubble: {
    position:      'relative',
    background:    'rgba(8, 18, 38, 0.88)',
    backdropFilter:'blur(20px) saturate(180%)',
    WebkitBackdropFilter:'blur(20px) saturate(180%)',
    border:        '1px solid rgba(255,255,255,0.10)',
    borderRadius:  14,
    padding:       '10px 32px 10px 14px',
    maxWidth:      220,
    marginBottom:  8,
    boxShadow:     '0 16px 36px rgba(2, 6, 23, 0.45)',
    pointerEvents: 'auto',
    animation:     'orboot-pop-in 300ms cubic-bezier(0.16, 1, 0.3, 1)',
  },
  bubbleMobile: {
    maxWidth: 200,
    padding: '9px 30px 9px 12px',
  },
  bubbleText: {
    fontSize:   12.5,
    fontWeight: 500,
    color:      '#f8fafc',
    lineHeight: 1.45,
    display:    'block',
    letterSpacing: '-0.005em',
  },
  dismiss: {
    position:   'absolute',
    top:        6,
    right:      6,
    background: 'rgba(255,255,255,0.08)',
    border:     'none',
    cursor:     'pointer',
    fontSize:   10,
    color:      '#cbd5e1',
    padding:    0,
    width:      20,
    height:     20,
    borderRadius: '50%',
    display:    'grid',
    placeItems: 'center',
    lineHeight: 1,
    pointerEvents: 'auto',
  },
  tail: {
    position:        'absolute',
    bottom:          -7,
    left:            22,
    width:           0,
    height:          0,
    borderLeft:      '7px solid transparent',
    borderRight:     '7px solid transparent',
    borderTop:       '7px solid rgba(8, 18, 38, 0.88)',
  },

  btn: {
    background:    'none',
    border:        'none',
    padding:       0,
    cursor:        'pointer',
    pointerEvents: 'auto',
    display:       'block',
    lineHeight:    0,
    transition:    'transform 0.25s cubic-bezier(0.34,1.56,0.64,1)',
  },

  fallback: {
    width:      DISPLAY_W,
    height:     DISPLAY_FRAME_H,
    fontSize:   56,
    display:    'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    filter:     'drop-shadow(0 8px 20px rgba(0,0,0,0.4))',
    animation:  'orboot-bob 1.6s ease-in-out infinite',
  },
};
